import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Transition Your Supporters When You Change Mission Fields or Agencies';
const DESCRIPTION =
    "Changing fields or sending organizations is normal for a missionary career, but it can look alarming to a donor who only sees the surface. A timeline and message structure for keeping supporters through the transition instead of losing them to it.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover25.jpg',
            datePublished: '2026-08-08',
            dateModified: '2026-08-08',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-transition-supporters-when-you-change-mission-fields',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How early should I tell donors I am changing fields or agencies?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "As soon as it is confirmed enough to be true and you are permitted to share it — usually 60 to 90 days before the change takes effect. Donors who hear about a change after it already happened, or from someone other than you, tend to read it as something being hidden rather than a normal transition.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do donors need to re-sign up if I switch sending agencies?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Usually yes. Recurring gifts are typically tied to the agency's own payment processing and tax-deductibility structure, so a change of agency commonly requires each donor to set up a new recurring gift rather than the old one simply carrying over — treat this as a required action item in your message, not a footnote.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Will I lose supporters when I change fields or agencies?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Some attrition is normal in any transition, but most of it is preventable — donors who leave usually do so because the reason for the change was unclear, not because the change itself was disqualifying. A clear, early, honest explanation keeps the large majority of supporters through the switch.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What if I am changing fields but staying with the same sending agency?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "That is a much simpler message — payment logistics and tax-deductibility usually stay the same, so the update can focus entirely on where you are going and why, without asking donors to take any new giving action.",
                    },
                },
            ],
        },
    ],
};

export default function HowToTransitionSupportersWhenYouChangeMissionFields() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-transition-supporters-when-you-change-mission-fields' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-transition-supporters-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover25.jpg" alt="A missionary packing boxes while writing an update letter to supporters" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Changing fields, roles, or sending organizations is a normal part of a long missionary career — but from a donor's seat, it can look like something went wrong. The transition itself rarely costs you supporters; an unclear or late explanation of it does. Handled well, a field or agency change can even become a reason for donors to re-engage.</p>

                    <h2>What donors actually worry about</h2>

                    <p>Donors aren't usually wondering whether your new assignment is a good idea — they're wondering three practical things: is this still legitimate, does my recurring gift still work, and do I need to do anything. If your message answers those three questions directly, most of the anxiety disappears. If it doesn't, donors fill the silence with their own guesses, and the guesses are rarely generous.</p>

                    <h2>Changing fields, same agency</h2>

                    <p>This is the simpler case. Payment logistics and tax-deductibility usually don't change, so your update can focus entirely on the new assignment: where you're going, why, and what stays the same about how supporters give. Say plainly that nothing needs to change on their end — that single sentence prevents a wave of "do I need to update anything?" replies.</p>

                    <h2>Changing sending agencies</h2>

                    <p>This is the case that needs the most care, because recurring gifts are typically tied to the old agency's payment processing — a new agency usually means donors have to set up a new recurring gift rather than the old one carrying over automatically, and tax-deductibility status can shift depending on the new agency's structure. Say this explicitly and early, give a specific date the old giving method stops working, and provide the new giving link well before that date so no one's support lapses by accident. If you're moving to a direct, agency-independent model instead, see <a href="/resources/sending-agency-vs-independent-missionary">sending agency vs. going independent</a> for the tax-deductibility trade-off to explain to donors up front.</p>

                    <h2>A 60-90 day transition timeline</h2>

                    <p>Announce as soon as it's confirmed and you're cleared to share it — don't wait for every detail to be settled. Roughly 45 days out, send a fuller explanation: why the change, what it means for giving, and the specific date anything donor-facing switches over. Around 14 days out, send a short reminder with the new giving link for anyone who hasn't acted yet. After the switch, send a short thank-you to everyone who moved their gift over, and a gentle personal follow-up to anyone who hasn't, before assuming they've decided not to continue.</p>

                    <h2>What to say when you don't know all the details yet</h2>

                    <p>You don't need every logistic finalized to tell donors something is coming. "We're transitioning to a new field/agency over the next few months — here's what we know now, and we'll follow up with exact dates and any action you need to take" is a complete, honest message. It's far better than staying silent until everything is perfectly settled, which is what usually turns a routine transition into a surprise.</p>

                    <h2>Common mistakes</h2>

                    <p>Letting donors find out from someone else, or from a newsletter that assumes they already know, damages trust faster than the change itself ever would. Burying the required action (re-confirming a recurring gift) inside a long narrative update means most people miss it entirely — put it in its own short paragraph or a bolded line. And going quiet during the actual transition period, when donors have the most questions, is when the most support quietly lapses — see <a href="/resources/what-to-do-when-a-major-donor-stops-giving">what to do when a major donor stops giving</a> if that's already happened and you're trying to win someone back.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How early should I tell donors?</strong><br />
                    As soon as it's confirmed and you're cleared to share it — usually 60-90 days before it takes effect.</p>

                    <p><strong>Do donors need to re-sign up if I switch agencies?</strong><br />
                    Usually yes, since recurring gifts are tied to the agency's payment processing — make this a clear action item, not a footnote.</p>

                    <p><strong>Will I lose supporters over this?</strong><br />
                    Some attrition is normal, but most of it comes from unclear communication, not the change itself.</p>

                    <p><strong>What if I'm staying with the same agency?</strong><br />
                    A much simpler message — focus on the new assignment, since giving logistics typically don't change.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/second-term-missionary-support-raising">Second-Term Missionary Support Raising</a></li>
                        <li><a href="/resources/missionary-furlough-support-raising">Missionary Furlough Support Raising</a></li>
                        <li><a href="/resources/sending-agency-vs-independent-missionary">Sending Agency vs. Going Independent</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. Recurring gifts on Devotion Fund live under your own giving page, not an agency's system — if you ever change organizations, your donor relationships and giving history move with you.</p>
                    <p><small><em>Last updated: 2026-08-08.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-transition-supporters-when-you-change-mission-fields" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
